// OnboardingTrialPromiseScreen — shown to iOS users right AFTER
// signup and BEFORE they hit the native paywall. Owner (Feb 2026)
// pivoted away from the "3 days free" message because many Apple
// regions don't honour the introductory trial on first install.
// We now advertise the limited-time `C10` offer code so users feel
// a concrete incentive to tap through to the subscribe sheet. The
// code is NOT auto-applied — it lives on the paywall's "I have a
// promo code" button so users still have to tap through Apple's
// Offer Code Redemption Sheet to redeem it (App Store policy).
//
// Visual language mirrors `OnboardingAndroidPromoScreen` so the
// cross-platform onboarding feels like one chapter.

import { motion } from "framer-motion";
import { ArrowRight, ArrowLeft, Ticket, Sparkles } from "lucide-react";

const COPY = {
  ar: {
    kicker: "عرض محدود للمشتركين الجدد 🎟️",
    headline: "كود خصم لمدة محدودة للمشتركين الجدد",
    sub: "فعّله في الصفحة التالية — اضغط «لدي رمز خصم» واكتب الكود.",
    codeLabel: "كود الخصم",
    bullets: [
      "خصم خاص للمشتركين الجدد",
      "دفع آمن عبر متجر آبل",
      "يمكنك الإلغاء في أي وقت",
    ],
    cta: "اذهب إلى صفحة الاشتراك",
    fineprint: "اكتب الكود يدوياً في الصفحة التالية — ما يُفعّل تلقائياً.",
  },
  en: {
    kicker: "Limited offer for new subscribers 🎟️",
    headline: "A limited-time discount code for new subscribers",
    sub: "Activate it on the next page — tap \"I have a promo code\" and enter it.",
    codeLabel: "Discount code",
    bullets: [
      "A special offer for new subscribers",
      "Secure payment via the App Store",
      "Cancel anytime",
    ],
    cta: "Continue to subscription",
    fineprint: "Enter the code manually on the next page — it is not auto-applied.",
  },
};

export default function OnboardingTrialPromiseScreen({ lang = "ar", onContinue }) {
  const isRTL = lang === "ar";
  const t = COPY[lang] || COPY.ar;

  return (
    <div
      dir={isRTL ? "rtl" : "ltr"}
      className="always-light fixed inset-0 z-[90] bg-white flex flex-col overflow-hidden"
      data-testid="onboarding-trial-screen"
    >
      {/* Soft warm gradient background */}
      <div
        aria-hidden
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(70% 50% at 50% 15%, rgba(255,213,150,0.55) 0%, rgba(255,255,255,0) 65%), linear-gradient(180deg, #FFF7EE 0%, #FFFFFF 55%, #FFFFFF 100%)",
        }}
      />

      {/* Sparkle motes */}
      <div aria-hidden className="absolute inset-x-0 top-0 h-1/2 pointer-events-none">
        {[...Array(10)].map((_, i) => (
          <span
            key={i}
            className="absolute text-amber-400/70"
            style={{
              left: `${(i * 79) % 100}%`,
              top: `${(i * 37) % 60}%`,
              fontSize: `${8 + (i % 3) * 3}px`,
              animation: `trialSparkle ${2.4 + (i % 4) * 0.5}s ease-in-out ${i * 0.2}s infinite`,
            }}
          >
            ✦
          </span>
        ))}
      </div>

      <style>{`
        @keyframes trialSparkle {
          0%, 100% { opacity: 0; transform: scale(0.6); }
          50%      { opacity: 1; transform: scale(1); }
        }
        @keyframes trialFloat {
          0%, 100% { transform: translateY(0); }
          50%      { transform: translateY(-5px); }
        }
      `}</style>

      <div
        className="relative z-10 flex-1 overflow-y-auto"
        style={{ paddingTop: "calc(env(safe-area-inset-top) + 24px)" }}
      >
        <div className="max-w-md mx-auto px-6 pb-40">
          {/* Big ticket disc */}
          <motion.div
            initial={{ scale: 0.65, opacity: 0, rotate: -6 }}
            animate={{ scale: 1, opacity: 1, rotate: 0 }}
            transition={{ type: "spring", damping: 14, stiffness: 200 }}
            className="flex justify-center mt-2"
          >
            <div
              className="relative w-40 h-40 rounded-full flex items-center justify-center"
              style={{
                background: "linear-gradient(145deg, #FFE08A 0%, #F5A623 55%, #C77800 100%)",
                boxShadow:
                  "0 22px 48px -12px rgba(199,120,0,0.55), inset 0 -6px 12px rgba(0,0,0,0.15), inset 0 6px 12px rgba(255,255,255,0.5)",
                animation: "trialFloat 4s ease-in-out infinite",
              }}
            >
              <Ticket className="w-20 h-20 text-white drop-shadow-lg" strokeWidth={2.2} />
              <Sparkles className="absolute -top-2 -right-2 w-6 h-6 text-yellow-200 fill-yellow-100 drop-shadow-lg" />
              <Sparkles className="absolute -bottom-1 -left-2 w-4 h-4 text-yellow-100 fill-yellow-50 drop-shadow" />
            </div>
          </motion.div>

          {/* Kicker pill */}
          <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25, duration: 0.4 }}
            className="flex justify-center mt-4"
          >
            <span className="inline-flex items-center gap-1.5 bg-brand-500 text-white text-[11px] font-black uppercase tracking-widest px-3 py-1.5 rounded-full shadow-sm">
              <Sparkles className="w-3.5 h-3.5 fill-white" strokeWidth={2.5} />
              {t.kicker}
            </span>
          </motion.div>

          {/* Headline */}
          <motion.h1
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35, duration: 0.4 }}
            className="text-2xl sm:text-3xl font-black text-neutral-900 text-center leading-tight mt-3 px-2"
          >
            {t.headline}
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.45, duration: 0.4 }}
            className="text-sm text-neutral-600 text-center mt-3 leading-relaxed px-3"
          >
            {t.sub}
          </motion.p>

          {/* The code — big, obvious, dashed border like a real coupon */}
          <motion.div
            initial={{ opacity: 0, scale: 0.92 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.55, duration: 0.4 }}
            className="mt-6 mx-auto max-w-xs"
          >
            <div className="bg-white rounded-2xl border-2 border-dashed border-brand-400 p-4 text-center shadow-sm">
              <div className="text-[10px] font-bold text-brand-600 uppercase tracking-widest mb-1.5">
                {t.codeLabel}
              </div>
              <div
                className="text-3xl sm:text-4xl font-black tracking-[0.3em] text-neutral-900 num"
                dir="ltr"
                data-testid="ios-promo-code"
              >
                CC10
              </div>
            </div>
          </motion.div>

          {/* Bullets */}
          <motion.ul
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.65, duration: 0.4 }}
            className="mt-6 space-y-3 text-[14.5px] text-neutral-700"
          >
            {t.bullets.map((b, i) => (
              <li key={i} className="flex items-start gap-2">
                <span className="mt-0.5 w-5 h-5 rounded-full bg-brand-500 text-white text-[11px] font-black flex items-center justify-center flex-shrink-0">
                  ✓
                </span>
                <span className="leading-[1.7]">{b}</span>
              </li>
            ))}
          </motion.ul>
        </div>
      </div>

      {/* Sticky CTA */}
      <div
        className="absolute inset-x-0 bottom-0 z-20 pointer-events-none"
        style={{
          paddingBottom: "calc(env(safe-area-inset-bottom) + 14px)",
          paddingTop: "26px",
          background:
            "linear-gradient(180deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.9) 40%, rgba(255,255,255,1) 100%)",
        }}
      >
        <div className="max-w-md mx-auto px-5 pointer-events-auto">
          <button
            type="button"
            onClick={onContinue}
            className="w-full orange-gradient text-white font-black py-4 rounded-full shadow-floating active:scale-[0.98] transition-transform text-base flex items-center justify-center gap-2"
            data-testid="onboarding-trial-continue"
          >
            {t.cta}
            {isRTL ? <ArrowLeft className="w-5 h-5" /> : <ArrowRight className="w-5 h-5" />}
          </button>
          <p className="text-[11.5px] text-neutral-500 text-center mt-2.5 leading-snug">
            {t.fineprint}
          </p>
        </div>
      </div>
    </div>
  );
}
